import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import BottomNav from '../components/BottomNav';
import BackButton from '../components/BackButton';
import ProductCard from '../components/ProductCard';
import FilterPanel from '../components/FilterPanel';
import { useLocation } from '../contexts/LocationContext';
import { useT } from '../i18n/I18nContext';
import { Loader2, Hash } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { location: userLocation } = useLocation();
  const { t } = useT();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    distanza_max: null,
    prezzo_min: null,
    prezzo_max: null,
    disponibile: false,
  });

  const query = searchParams.get('q') || '';
  const tagFilter = searchParams.get('tag') || '';

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (query) params.append('q', query);
      if (tagFilter) params.append('tag', tagFilter);
      if (userLocation) {
        params.append('lat', userLocation.lat);
        params.append('lng', userLocation.lng);
      }
      if (filters.distanza_max) params.append('distanza_max', filters.distanza_max);
      if (filters.prezzo_min) params.append('prezzo_min', filters.prezzo_min);
      if (filters.prezzo_max) params.append('prezzo_max', filters.prezzo_max);
      if (filters.disponibile) params.append('disponibile', 'true');
      params.append('limit', '50');

      const response = await axios.get(`${API}/products?${params.toString()}`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error searching products:', error);
    } finally {
      setLoading(false);
    }
  }, [query, tagFilter, userLocation, filters]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleSearch = (newQuery) => {
    setSearchParams({ q: newQuery });
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleApplyFilters = () => {
    fetchProducts();
  };

  const clearTagFilter = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('tag');
    setSearchParams(newParams);
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-24" data-testid="search-page">
      <Header onSearch={handleSearch} searchQuery={query} />

      <main className="px-4 py-4 max-w-7xl mx-auto">
        <BackButton fallback="/home" className="mb-3" />
        {/* Results header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            {tagFilter ? (
              <div className="flex items-center gap-2">
                <h1 className="font-outfit font-semibold text-lg text-slate-800" data-testid="tag-results-title">
                  <Hash className="w-5 h-5 inline text-blue-600" />
                  {tagFilter}
                </h1>
                <button
                  onClick={clearTagFilter}
                  className="text-xs bg-slate-200 hover:bg-slate-300 text-slate-600 px-2 py-1 rounded-full"
                >
                  {t('search.remove_filter')}
                </button>
              </div>
            ) : query ? (
              <h1 className="font-outfit font-semibold text-lg text-slate-800" data-testid="search-results-title">
                {t('search.results_for')} <span className="text-blue-600">{query}</span>
              </h1>
            ) : (
              <h1 className="font-outfit font-semibold text-lg text-slate-800">
                {t('search.all_products')}
              </h1>
            )}
            <p className="text-sm text-slate-500">
              {products.length} {products.length === 1 ? t('common.results_one') : t('common.results_other')}
            </p>
          </div>

          <FilterPanel
            filters={filters}
            onChange={handleFilterChange}
            onApply={handleApplyFilters}
          />
        </div>

        {/* Products grid */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : products.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 pb-8" data-testid="search-results-grid">
            {products.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        ) : (
          <div className="empty-state bg-white rounded-2xl">
            <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mb-4">
              <span className="text-4xl">🔍</span>
            </div>
            <h3 className="font-outfit font-semibold text-slate-700 mb-2">
              {t('search.empty_title')}
            </h3>
            <p className="text-slate-500 text-sm">
              {t('search.empty_msg')}
            </p>
          </div>
        )}
      </main>

      <BottomNav />
    </div>
  );
}
